"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.formatPillValue = exports.formatAbsoluteValue = exports.formatValueAsUnsignedPercentage = exports.formatValueAsRelativePercentage = exports.formatValueAsRelativeNumber = exports.formatValueAsMetricNumber = void 0;
var formatParser_1 = require("./formatParser");
var Localize_1 = require("../../gen-src/Localize");
var localization_1 = require("../localization");
var models_1 = require("../models");
var DEFAULT_PILL_CHARACTERS = 10;
var IncludeSign;
(function (IncludeSign) {
    IncludeSign[IncludeSign["NEVER"] = 0] = "NEVER";
    IncludeSign[IncludeSign["NEGATIVE"] = 1] = "NEGATIVE";
    IncludeSign[IncludeSign["ALWAYS"] = 2] = "ALWAYS";
})(IncludeSign || (IncludeSign = {}));
function formatValueAsMetricNumber(value, formatString, maxCharacters) {
    return formatMetricValueForDisplay(value, formatString || '', IncludeSign.NEGATIVE, maxCharacters);
}
exports.formatValueAsMetricNumber = formatValueAsMetricNumber;
function formatValueAsRelativeNumber(value, formatString, maxCharacters) {
    return formatMetricValueForDisplay(value, formatString || '', IncludeSign.ALWAYS, maxCharacters);
}
exports.formatValueAsRelativeNumber = formatValueAsRelativeNumber;
// Note: we don't want to use a format string for e.g. currency symbol, since this is always a percentage.
function formatValueAsRelativePercentage(value, maxCharacters) {
    return formatMetricValueForDisplay(value, 'p', IncludeSign.ALWAYS, maxCharacters);
}
exports.formatValueAsRelativePercentage = formatValueAsRelativePercentage;
// Note: we don't want to use a format string for e.g. currency symbol, since this is always a percentage.
function formatValueAsUnsignedPercentage(value, maxCharacters) {
    return formatMetricValueForDisplay(value, 'p', IncludeSign.NEVER, maxCharacters);
}
exports.formatValueAsUnsignedPercentage = formatValueAsUnsignedPercentage;
function formatAbsoluteValue(value, formatString, maxCharacters) {
    return formatMetricValueForDisplay(value, formatString || '', IncludeSign.NEVER, maxCharacters);
}
exports.formatAbsoluteValue = formatAbsoluteValue;
var formatPillValue = function (v, formatString, maxCharacters) {
    maxCharacters = maxCharacters || DEFAULT_PILL_CHARACTERS;
    // tslint:disable-next-line: switch-default
    switch (v.role) {
        case models_1.MetricValueRole.CURRENT:
            return formatValueAsMetricNumber(v.value, formatString, maxCharacters);
        case models_1.MetricValueRole.ABSOLUTE_CHANGE:
            return formatValueAsRelativeNumber(v.value, formatString, maxCharacters);
        case models_1.MetricValueRole.PERCENTAGE_CHANGE:
            return formatValueAsRelativePercentage(v.value, maxCharacters);
    }
};
exports.formatPillValue = formatPillValue;
/**
 * The below code formats a number value for display as a metric. The following requirements are considered:
 * - Glanceability: Easy-to-understand scale. Number types, such as currency and percentage are present.
 * - Compactness: Ability to fit in a limited display space - phones, pills, axis ticks, etc.
 * - Correctness: Display accurately for the user's device and locale.
 *
 * To format the number we break it into four parts: sign, prefix, value, and postfix.
 * - sign: '+', '-', or '', depending on the sign of the number and the value of the 'includeSign' parameter.
 * - prefix: currency symbol, custom user input, or empty
 * - value: compact number, e.g. 1,200,000 => 1.2M.  We default to a max of two decimal places.
 * - postfix: same as prefix, but after the value.
 *
 * The prefix and postfix strings are parsed from the formatString if present.
 *
 * Once this is done we sum up the length of the parts.  If the length is not greater than the maxCharacters,
 * we concatenate the parts return the value string as-is.
 *
 * If the string is too long, we do the following:
 * - try to compact the value further by removing any decimals.
 * - trim and truncate the prefix and postfix strings.
 *
 * In all cases we keep the sign and enough of the value to accurately display the value of the number, even if
 * this means we can't fit it in the desired maxCharacters.
 */
/**
 * This function formats the value as a number with currency/percentage applied.
 * @param value The number to be formatted.
 * @param formatString A format string containing currency and other information.
 * @param includeSign specifies whether the sign of the number (+/-) should be included.
 * @param maxCharacters An optional number specifying the max number of characters for the string.
 *                 If this is non-zero, the value will be fit into this number if possible.
 */
function formatMetricValueForDisplay(value, formatString, includeSign, maxCharacters) {
    if (value === undefined) {
        return localization_1.Messages.metricsNoValue();
    }
    // Determine the sign string from the value and the includeSign parameter.
    var sign = '';
    if (includeSign !== IncludeSign.NEVER) {
        if (value < 0) {
            sign = '-';
        }
        else if (includeSign === IncludeSign.ALWAYS) {
            sign = '+';
        }
    }
    // Parse the format string, and get the prefix and postfix strings if present.
    var prefix = '';
    var postfix = '';
    var isCurrency = false;
    var isPercentage = false;
    var formatInfo = (0, formatParser_1.parseFormatString)(formatString);
    if (formatInfo.dataType === formatParser_1.FormatDataType.FORMAT_NUMBER) {
        prefix = formatInfo.prefixSymbol;
        postfix = formatInfo.postfixSymbol;
        isCurrency = formatInfo.isCurrency;
        isPercentage = formatInfo.isPercentage;
    }
    // Format as a scaled positive number with grouping separators and round to max 2 decimal places.
    var scaledValueForDisplay = scaleAndFormatNumber(value, false /* don't truncate decimals */, isCurrency, isPercentage);
    // If needed, truncate the fractional part of the number and the prefix/postfix strings.
    if (maxCharacters && maxCharacters < scaledValueForDisplay.length + sign.length + prefix.length + postfix.length) {
        var truncatedValue = scaleAndFormatNumber(value, true /* do truncate decimals */, isCurrency, isPercentage);
        scaledValueForDisplay = fitDisplayValueToWidthInCharacters(truncatedValue, sign, prefix, postfix, maxCharacters);
    }
    else {
        scaledValueForDisplay = "".concat(sign).concat(prefix).concat(scaledValueForDisplay).concat(postfix);
    }
    return scaledValueForDisplay;
}
/**
 * This method returns the scaled absolute value of the number, including the scale specifier.
 * @param value The number to be scaled
 * @param truncateDecimals if true, no decimals will be included in the string.
 */
function scaleAndFormatNumber(value, truncateDecimals, isCurrency, isPercentage) {
    // Use the absolute value when determining the scale of the number
    var absValue = Math.abs(value);
    // Percentages are multiplied by a hundred when they are formatted. We want to preview the size
    // of the number to decide how we want to format it.
    var scaledAbsValue = absValue * (isPercentage ? 100 : 1);
    if (scaledAbsValue < 0.01) {
        // Currently we only scale large numbers.
        // Treat small numbers as 0 because we don't know how to render them.
        return isPercentage ? Localize_1.Formatters.formatPercentageWithNoDecimals(0) : Localize_1.Formatters.formatNumberWithNoDecimals(0);
    }
    if (scaledAbsValue < 10000 && isInteger(scaledAbsValue)) {
        // The raw number looks better than the scaled version
        return isPercentage
            ? Localize_1.Formatters.formatPercentageWithNoDecimals(absValue)
            : Localize_1.Formatters.formatNumberWithNoDecimals(absValue);
    }
    if (truncateDecimals) {
        return isPercentage
            ? Localize_1.Formatters.formatCompactPercentageWithNoDecimals(absValue)
            : Localize_1.Formatters.formatCompactNumberWithNoDecimals(absValue);
    }
    var compacted = isPercentage
        ? Localize_1.Formatters.formatCompactPercentageWithMaxTwoDecimals(absValue)
        : Localize_1.Formatters.formatCompactNumberWithMaxTwoDecimals(absValue);
    var maxTwoDecimals = isPercentage
        ? Localize_1.Formatters.formatPercentageWithMaxTwoDecimals(absValue)
        : Localize_1.Formatters.formatNumberWithMaxTwoDecimals(absValue);
    if (isCurrency && compacted === maxTwoDecimals) {
        return isPercentage
            ? Localize_1.Formatters.formatCompactPercentageWithExactlyTwoDecimals(absValue)
            : Localize_1.Formatters.formatCompactNumberWithExactlyTwoDecimals(absValue);
    }
    // return a compact (aka scaled) version of the number
    return compacted;
}
function fitDisplayValueToWidthInCharacters(numberValue, sign, prefix, postfix, width) {
    // The numberValue has already been trimmed if possible; so if the string doesn't fit in the width,
    // we need to trim the prefix and postfix strings.
    var signLength = sign.length;
    var valueLength = numberValue.length;
    var prefixLength = prefix.length;
    var postfixLength = postfix.length;
    if ((prefixLength || postfixLength) && signLength + valueLength + prefixLength + postfixLength > width) {
        // Let's distribute any remaining width between the prefix and postfix.
        var remainingWidth = Math.max(width - valueLength - signLength, 1);
        var newPrefixLength = Math.ceil((prefixLength / (prefixLength + postfixLength)) * remainingWidth);
        var newPostfixLength = Math.ceil((postfixLength / (prefixLength + postfixLength)) * remainingWidth);
        if (prefix.length > newPrefixLength) {
            prefix = prefix.trim().substr(0, newPrefixLength);
        }
        if (postfix.length > newPostfixLength) {
            postfix = postfix.trim().substr(0, newPostfixLength);
        }
    }
    return sign + prefix + numberValue + postfix;
}
var isInteger = function (value) {
    return typeof value === 'number' && isFinite(value) && Math.floor(value) === value;
};
//# sourceMappingURL=numberFormatter.js.map